import _ from 'lodash';
import Fuse from 'fuse.js';

import { properCase, getContainingFeature } from './helpers';

export default class RegionModel {
    /**
     * params = Data needed by the region model: lgaGeojson, postalLookup, zoneLookup, municipalities
     */
    constructor(data, geolocation) {
        // Data
        this.data = data;
        this.defaultRegion = 'Bonnie Doon';
        // Local state
        this.localCode = null;
        this.geolocation = geolocation;
        this.searchError = null;
        this.loading = null;
    }

    get locationPermission() {
        return this.geolocation && this.geolocation.status;
    }

    async tryUseCurrentLocation() {
        this.loading = 'Device Location';
        setTimeout(async () => {
            try {
                const posCoords = await this.geolocation.getCurrentPosition();
                this.loading = `${posCoords}`;
                this.setRegionFromCoords(posCoords);
            } catch (err) {
                console.warn('Error trying using current location:', err);
            }
            this.loading = null;
        }, 1);
    }

    get locationError() {
        if (this.geolocation.error) {
            return `${this.geolocation.error} Please manually search for a location.`;
        }
        return null;
    }

    async useDefault() {
        if (this.locationPermission === 'granted') {
            this.tryUseCurrentLocation();
        } else {
            this.selectRegion(this.defaultRegion);
        }
    }

    get baseGeojson() {
        return this.data.baseGeojson;
    }

    get zonesGeojson() {
        return this.data.zonesGeojson;
    }

    get lgaGeojson() {
        return this.data.lgaGeojson;
    }

    get compareMapGeojson() {
        return this.data.compareMapGeojson;
    }

    get highlightMapGeojson() {
        return this.data.highlightMapGeojson;
    }

    get geographyNameLookup() {
        return Object.freeze({
            ..._(this.data.postalLookup).keyBy('localCode').mapValues(({ localName }) => properCase(localName)).value(),
            ..._(this.data.postalLookup).keyBy('zoneCode').mapValues(({ zoneName }) => properCase(zoneName)).value(),
            AB: 'Alberta'
        });
    }

    get urbanRuralClassLookup() {
        return Object.freeze({
            ..._(this.data.lgaClassLookup)
                .keyBy('localCode')
                .mapValues(({ urbanRuralType }) => properCase(urbanRuralType))
                .value(),
        });
    }

    get loadingMessage() {
        if (!this.loading) {
            return null;
        }
        let loading = this.loading;
        // PostalCode, leave as is
        if (!loading.match(/^[ABCEGHJKLMNPRSTVXYabceghjklmnprstvxy]{1}\d{1}[A-Za-z]{1}[ ]{0,1}\d{1}[A-Za-z]{1}\d{1}$/)) {
            loading = _.startCase(this.loading.toLowerCase());
        }
        return `Loading ${loading}`;
    }

    get localMunicipalities() {
        return Object.freeze(this.data.municipalities.filter(({ lga }) => this.localCode === lga)
            .map(({ name, ...rest }) => ({ name: properCase(name), ...rest })));
    }

    get region() {
        if (!this.localCode) {
            return null;
        }
        const region = _.find(this.data.postalLookup, { localCode: this.localCode });
        if (!region) {
            this.localCode = null;
            return null;
        }
        const urbanRuralClass = this.data.lgaClassLookup
            .find((item) => item.localCode === this.localCode)
            .urbanRuralType;
        return {
            ...region,
            localName: properCase(region.localName),
            zoneName: properCase(region.zoneName),
            urbanRuralClass: properCase(urbanRuralClass)
        };
    }

    get allLocalCodes() {
        return Object.freeze(_(this.data.postalLookup).map('localCode').uniqBy().value());
    }

    async selectRegion(region) {
        this.searchError = null;
        // await (async () => {
        if (!region) {
            this.localCode = null;
            return;
        }
        this.loading = `${region}`;
        setTimeout(() => {
            const results = this.searchForRegion(region);
            if (results.length > 0) {
                this.localCode = results[0].item.localCode;
                this.searchError = null;
            } else {
                this.searchError = 'Couldn\'t find matching region';
            }
            this.loading = null;
        }, 1);
    }

    clearSearchError() {
        this.searchError = null;
    }

    clearRegion() {
        this.selectRegion(null);
    }

    setRegionFromCoords({ coords }) {
        const matchingFeature = getContainingFeature(this.data.lgaGeojson, coords);

        if (matchingFeature) {
            this.localCode = matchingFeature.properties.LOCAL_CODE;
        } else {
            this.searchError = 'Couldnt find matching region';
        }
    }

    searchForRegion(region) {
        const postalCodeSearch = new Fuse(this.data.postalLookup, {
            keys: ['postalcode'],
            includeScore: true,
            includeMatches: true,
            threshold: 0.15
            // "a1a 1a1" matches "a1a1a1" with a score of 0.1428
            // "a1a1a2" matches "a1a1a1" with a score of 0.16
            //
            // So a threshold of 0.15 allows us to match with a space, but not with a different character
        });
        const zoneSearch = new Fuse(this.data.zoneLookup, {
            keys: ['localName', 'municipalityFullName'],
            includeScore: true,
            includeMatches: true,
            threshold: 0.2
        });

        const postalCodeResults = postalCodeSearch.search(region);
        if (postalCodeResults.length === 1) {
            return postalCodeResults.map(({ score, matches, item }) => ({ score, matches, item, value: item.postalcode }));
        }
        return _(zoneSearch.search(region))
            .uniqBy(row => row.item.localCode)
            .map(({ score, matches, item }) => {
                // Add a very slight penalty if we didn't match the localName
                // So that when we search for an exact localName, those results are first
                if (!_.find(matches, { key: 'localName' })) {
                    // eslint-disable-next-line no-param-reassign
                    score += 0.00000001;
                }
                return {
                    score,
                    matches,
                    item,
                    value: item.localName,
                    secondaryValue: item.municipalityFullName
                };
            })
            .sortBy('score')
            .slice(0, 20)
            .value();
    }
}
